import { useEffect, useMemo, useState } from "react";
import { Link, useRoute } from "wouter";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowUpRight,
  CalendarDays,
  Check,
  Instagram,
  MapPin,
  MessageSquare,
  Pencil as PencilIcon,
  Lock,
  LockOpen,
  Send,
  Trash2,
  Undo2,
  X,
} from "lucide-react";
import {
  useKikiDetail,
  useDeleteBuddyRequest,
  useSetKikiStatus,
  useUpdateBuddyApplicationStatus,
  useWithdrawBuddyApplication,
  type BuddyApplicantSummary,
  type BuddyApplicationStatus,
  type BuddyRequestDetail,
  type KikiActivityInfo,
  type KikiEventContext,
} from "../lib/api/useEventBuddies";
import { useAuth } from "../lib/platform";
import { OnboardingProvider, useOnboardingGate } from "../components/OnboardingContext";
import { ProfileImage } from "../components/ProfileImage";
import { PublicProfileButton } from "../components/PublicProfileModal";
import { ReachOutModal } from "../components/ReachOutModal";
import { EventPoster } from "../components/EventPoster";
import { getKikiCategoryByEmoji, DEFAULT_KIKI_GRADIENT } from "../lib/kikiTemplates";
import { DateBadge } from "../components/ux/DateBadge";
import { parseUTCDate, formatTimeToDisplay } from "../utils/dateUtils";
import { sanitizeRichText } from "../utils/sanitizeHtml";
import { getEventType } from "@shared/eventTypes";
import { formatNameWithInitial } from "@shared/formatName";
import { useToast } from "../components/ux/Toast";
import { useConfirm } from "../components/ConfirmModal";
import { Loader } from "@/components/Loader";
import Logo from "@/components/Logo";
import tracking from "../lib/tracking";

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function KikiPage() {
  // Provider so signed-out gating opens the onboarding explainer (what Outclose
  // is + sign up), falling back to plain sign-in only outside this tree.
  return (
    <OnboardingProvider>
      <KikiPageContent />
    </OnboardingProvider>
  );
}

function KikiPageContent() {
  // Kikis live on their own URL now; still match the legacy slugged path.
  const [, idParams] = useRoute("/kiki/:id");
  const [, legacyParams] = useRoute("/events/:slug/kiki/:requestId");
  const requestId = idParams?.id ?? legacyParams?.requestId ?? null;

  const { data, isLoading } = useKikiDetail(requestId);
  const { userId, isSignedIn, openSignIn } = useAuth();
  const onboarding = useOnboardingGate();
  // Signed-out gating → onboarding explainer (returns to this kiki after signup).
  const gateSignIn = () =>
    onboarding ? onboarding.openOnboarding(requestId ? `/kiki/${requestId}` : undefined) : openSignIn();

  const detail = data?.success ? data.data : null;
  const event = detail?.event ?? null;
  const activity = detail?.activityInfo ?? null;

  // Header name comes from the linked event, or the standalone activity.
  const headerName = event?.name ?? activity?.name ?? "Kiki";

  // Real event slug (for back/edit links) vs. the value passed to the
  // mutation hooks — those key off the kiki id, so a placeholder is fine.
  const eventSlug = event?.slug ?? null;
  const apiSlug = eventSlug ?? "_";
  const backHref = eventSlug ? `/events/${eventSlug}` : "/events";

  useEffect(() => {
    if (detail) document.title = `Kiki · ${headerName} — Outclose`;
    return () => { document.title = "Outclose"; };
  }, [detail, headerName]);

  // Page-view event — fire once the kiki resolves, keyed on its id so it logs
  // once per kiki rather than on every viewerStatus/refetch change.
  useEffect(() => {
    if (detail) tracking.kikiPageViewed(detail.id, detail.viewerStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="max-w-md mx-auto p-6 text-center min-h-[60vh] flex flex-col justify-center">
        <h1 className="text-2xl font-bold mb-2">Not found</h1>
        <p className="text-neutral-500 dark:text-neutral-400 mb-4">
          This kiki doesn't exist or has been removed.
        </p>
        <Link href="/events" className="text-primary-500 hover:underline text-sm">
          ← Browse events
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col pb-safe-inset">
      <Logo withIcon={true} className="md:hidden justify-center pt-4 mb-4" />

      {/* Back nav */}
      <div className="max-w-2xl mx-auto w-full px-4 sm:px-6 pt-2 pb-2">
        <Link
          href="/events"
          className="inline-flex items-center gap-1.5 text-sm text-neutral-500 dark:text-neutral-400 hover:text-primary-500 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          All events
        </Link>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="max-w-2xl mx-auto w-full px-4 sm:px-6 space-y-4 flex-1 flex flex-col max-md:pb-8"
      >
        {/* Kiki front and center — the person, their note, and the CTA */}
        <KikiDetailCard
          detail={detail}
          slug={apiSlug}
          eventSlug={eventSlug}
          backHref={backHref}
          requestId={requestId!}
          userId={userId}
          isSignedIn={isSignedIn}
          openSignIn={gateSignIn}
        />

        {/* Supporting context — what they'd be going to */}
        <div>
          <p className="px-1 mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-400 dark:text-neutral-500">
            {event ? "The event" : "What they're up for"}
          </p>
          <EventDetailsCard event={event} activity={activity} />
        </div>

        <div className="pb-8" />
      </motion.div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Event details — rich hero shown for both linked events and inline activities
// ---------------------------------------------------------------------------

function EventDetailsCard({
  event,
  activity,
}: {
  event: KikiEventContext | null;
  activity: KikiActivityInfo | null;
}) {
  const name = event?.name ?? activity?.name ?? "Kiki";
  const dateStr = event?.date ?? activity?.date ?? null;
  const timeStr = event?.time ?? activity?.time ?? null;

  const parsedDate = useMemo(() => (dateStr ? parseUTCDate(dateStr) : null), [dateStr]);
  const timeLabel = useMemo(() => {
    if (!timeStr) return null;
    const start = formatTimeToDisplay(timeStr);
    if (!event?.endTime) return start;
    return `${start} – ${formatTimeToDisplay(event.endTime)}`;
  }, [timeStr, event]);

  const venueName = event?.venue.name ?? null;
  const venueAddress = event?.venue.address ?? null;
  const venueLocation = event
    ? [event.venue.neighborhood, event.venue.city].filter(Boolean).join(", ")
    : (activity?.location ?? null);
  const mapHref = venueAddress
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        `${venueName ? `${venueName}, ` : ""}${venueAddress}`,
      )}`
    : null;

  const types = event?.types ?? [];
  // Match the carousel card: resolve the standalone kiki's poster from its emoji
  // — a category photo when one exists, otherwise the category/brand gradient.
  const kikiCategory = getKikiCategoryByEmoji(activity?.emoji);

  return (
    <div className="card !p-0 overflow-hidden">
      {/* Cover / hero */}
      {event ? (
        <div className="relative aspect-[16/10] bg-neutral-900">
          {event.coverImageUrl ? (
            <img
              src={event.coverImageUrl}
              alt={name}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <EventPoster name={name} label={event.posterLabel} types={types} />
          )}
        </div>
      ) : activity?.emoji ? (
        kikiCategory?.image ? (
          <div className="relative aspect-[16/10] bg-neutral-900">
            <img
              src={kikiCategory.image}
              alt={name}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className={`flex items-center justify-center aspect-[16/10] bg-gradient-to-br ${kikiCategory?.gradient ?? DEFAULT_KIKI_GRADIENT} text-7xl`}>
            <span style={{ filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.35))" }}>{activity.emoji}</span>
          </div>
        )
      ) : null}

      {/* Title */}
      <div className="px-5 sm:px-6 pt-5 pb-3">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight leading-tight">{name}</h1>
      </div>

      {/* Date + time */}
      {(parsedDate || timeLabel) && (
        <div className="px-5 sm:px-6 py-4 border-t border-black/10 dark:border-white/10 flex items-center gap-4">
          {parsedDate && dateStr ? (
            <DateBadge date={dateStr} size="xl" />
          ) : (
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-base-200 dark:bg-neutral-800 shrink-0">
              <CalendarDays className="w-5 h-5 text-neutral-400" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold leading-none">
              {parsedDate?.weekday ?? "Date to be decided"}
              {timeLabel && (
                <span className="font-normal text-neutral-500 dark:text-neutral-400"> · {timeLabel}</span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Tags */}
      {types.length > 0 && (
        <div className="px-5 sm:px-6 py-4 border-t border-black/10 dark:border-white/10">
          <div className="flex flex-wrap gap-2">
            {types.map((slug) => {
              const t = getEventType(slug);
              if (!t) return null;
              return (
                <span
                  key={slug}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-base-200 dark:bg-neutral-800 text-sm font-medium"
                >
                  <span>{t.emoji}</span>
                  <span>{t.label}</span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Description */}
      {event?.description && (
        <div className="px-5 sm:px-6 py-4 border-t border-black/10 dark:border-white/10">
          <div
            className="prose prose-sm dark:prose-invert max-w-none leading-relaxed"
            dangerouslySetInnerHTML={{ __html: sanitizeRichText(event.description) }}
          />
        </div>
      )}

      {/* Venue */}
      {(venueName || venueLocation) && (
        mapHref ? (
          <a
            href={mapHref}
            target="_blank"
            rel="noopener noreferrer"
            className="px-5 sm:px-6 py-4 border-t border-black/10 dark:border-white/10 flex items-start gap-3 group"
          >
            <MapPin className="w-5 h-5 text-primary-500 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              {venueName && <div className="font-medium">{venueName}</div>}
              <div className="text-sm text-neutral-500 dark:text-neutral-400 group-hover:text-primary-500 transition-colors">
                {venueAddress}
              </div>
            </div>
            <ArrowUpRight className="w-4 h-4 text-neutral-400 shrink-0 mt-0.5" />
          </a>
        ) : (
          <div className="px-5 sm:px-6 py-4 border-t border-black/10 dark:border-white/10 flex items-start gap-3">
            <MapPin className="w-5 h-5 text-primary-500 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              {venueName && <div className="font-medium">{venueName}</div>}
              {venueLocation && (
                <div className="text-sm text-neutral-500 dark:text-neutral-400">{venueLocation}</div>
              )}
            </div>
          </div>
        )
      )}

      {/* Link through to the full event page */}
      {event?.slug && (
        <Link
          href={`/events/${event.slug}`}
          className="px-5 sm:px-6 py-3.5 border-t border-black/10 dark:border-white/10 flex items-center justify-between text-sm font-medium text-primary-500 hover:bg-base-200/50 dark:hover:bg-neutral-800/40 transition-colors no-underline"
        >
          View full event
          <ArrowUpRight className="w-4 h-4" />
        </Link>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Kiki detail card — poster profile + role-aware action area
// ---------------------------------------------------------------------------

function KikiDetailCard({
  detail,
  slug,
  eventSlug,
  backHref,
  requestId,
  userId,
  isSignedIn,
  openSignIn,
}: {
  detail: BuddyRequestDetail;
  slug: string;
  eventSlug: string | null;
  backHref: string;
  requestId: string;
  userId: string | null;
  isSignedIn: boolean;
  openSignIn: () => void;
}) {
  const interests = detail.preferences?.interests ?? [];
  const isOwner = detail.viewerStatus === "owner";

  // A kiki is a group going together — the host plus everyone confirmed.
  const members: KikiMember[] = [
    { user: detail.user, isHost: true },
    ...detail.acceptedApplicants
      .filter((a) => a.id !== detail.user.id)
      .map((user) => ({ user, isHost: false })),
  ];

  // Owner-only: edit/delete/close controls + the group chat once confirmed.
  const del = useDeleteBuddyRequest(slug);
  const setStatus = useSetKikiStatus(slug);
  const { confirm } = useConfirm();
  const toast = useToast();
  const acceptedCount = (detail.ownerApplications ?? []).filter((a) => a.status === "accepted").length;
  const isClosed = !!detail.status && detail.status !== "active";

  const handleToggleClose = async () => {
    try {
      const nextStatus = isClosed ? "active" : "fulfilled";
      const res = await setStatus.mutateAsync({ id: detail.id, status: nextStatus });
      if (!res.success) { toast.error("Couldn't update"); return; }
      tracking.kikiStatusToggled(detail.id, nextStatus);
      toast.success(isClosed ? "Kiki reopened" : "Kiki closed");
    } catch {
      toast.error("Couldn't update");
    }
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: "Delete your kiki?",
      message: "This will also remove anyone who reached out.",
      confirmText: "Delete",
      type: "danger",
    });
    if (!ok) return;
    try {
      const res = await del.mutateAsync(detail.id);
      if (!res.success) { toast.error("Couldn't delete"); return; }
      tracking.kikiDeleted(detail.id);
      window.location.href = backHref;
    } catch {
      toast.error("Couldn't delete");
    }
  };

  return (
    <div className="card !p-0 overflow-hidden">
      {/* Members — everyone in the kiki, host badged */}
      <div className="px-5 sm:px-6 pt-5 pb-5">
        <div className="flex items-start justify-between gap-3 pb-2">
          <div className="min-w-0">
            <h2 className="text-xl font-bold tracking-tight">
              Let's have a kiki
              {isClosed && (
                <span className="ml-2 align-middle inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-base-200 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 text-[11px] font-semibold">
                  <Lock className="w-3 h-3" /> Closed
                </span>
              )}
            </h2>
          </div>
          {isOwner && (
            <div className="shrink-0 flex items-center gap-1">
              {eventSlug && (
                <Link
                  href={`/events/${eventSlug}`}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs text-neutral-500 hover:text-primary-500 hover:bg-base-200/60 dark:hover:bg-neutral-800/60 transition-colors"
                >
                  <PencilIcon className="w-3.5 h-3.5" /> Edit
                </Link>
              )}
              <button
                type="button"
                onClick={handleToggleClose}
                disabled={setStatus.isPending}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs text-neutral-500 hover:text-primary-500 hover:bg-base-200/60 dark:hover:bg-neutral-800/60 transition-colors disabled:opacity-50"
              >
                {isClosed ? <LockOpen className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                {isClosed ? "Reopen" : "Close"}
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={del.isPending}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs text-neutral-500 hover:text-rose-500 hover:bg-rose-500/10 transition-colors disabled:opacity-50"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            </div>
          )}
        </div>
        {isSignedIn ? (
          <MemberRoster members={members} currentUserId={userId} />
        ) : (
          <div className="relative">
            <div className="blur-[6px] pointer-events-none select-none" aria-hidden>
              <MemberRoster members={members} currentUserId={userId} />
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <button
                type="button"
                onClick={() => { tracking.kikiSignInClicked("detail"); openSignIn(); }}
                className="px-4 py-2 rounded-full bg-white/95 dark:bg-neutral-900/90 text-neutral-900 dark:text-white text-sm font-semibold shadow-lg backdrop-blur ring-1 ring-black/10"
              >
                Sign in to see who's going
              </button>
            </div>
          </div>
        )}
        {detail.note && (
          <p className="mt-4 border-l-2 border-primary-500/40 pl-3 text-sm leading-relaxed text-neutral-600 dark:text-neutral-300">
            {detail.note}
          </p>
        )}
        {interests.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {interests.map((i) => (
              <span
                key={i}
                className="px-2.5 py-0.5 rounded-full bg-base-200 dark:bg-neutral-800 text-xs text-neutral-700 dark:text-neutral-200"
              >
                {i}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Group chat — full-width, right under the top section */}
      {isOwner && acceptedCount > 0 && (
        <div className="px-5 sm:px-6 pb-5">
          <KikiChatButton href={`/app/chat/kiki_${requestId}`} label="Group chat" kikiId={requestId} chatType="group" />
        </div>
      )}

      {/* Action area — shares the card's section rhythm */}
      <div className="px-5 sm:px-6 py-5 border-t border-black/10 dark:border-white/10">
        {detail.viewerStatus === "owner" && (
          <OwnerActions detail={detail} slug={slug} userId={userId} />
        )}
        {detail.viewerStatus === "accepted" && (
          <div className="space-y-3">
            <AcceptedBadge posterName={detail.user.firstName} />
            <KikiChatButton href={`/app/chat/kiki_${requestId}`} label="Open kiki chat" kikiId={requestId} chatType="group" />
          </div>
        )}
        {detail.viewerStatus === "pending" && detail.viewerApplication && (
          <PendingActions
            slug={slug}
            requestId={requestId}
            applicationId={detail.viewerApplication.id}
          />
        )}
        {(detail.viewerStatus === "declined" || detail.viewerStatus === "visitor") && (
          <VisitorActions detail={detail} slug={slug} isSignedIn={isSignedIn} openSignIn={openSignIn} />
        )}
      </div>
    </div>
  );
}

type KikiMember = { user: BuddyApplicantSummary; isHost: boolean };

// Everyone in the kiki as a roster — host gets a badge, the rest are confirmed
// members. Frames the kiki as a group going together, not one person's profile.
function MemberRoster({
  members,
  currentUserId,
}: {
  members: KikiMember[];
  currentUserId: string | null;
}) {
  return (
    <div className="space-y-2.5">
      {members.map(({ user, isHost }) => (
        <div key={user.id} className="flex items-center gap-3">
          <PublicProfileButton
            userId={user.id}
            ariaLabel={`View ${user.firstName}'s profile`}
            className="flex items-center gap-3 min-w-0 text-left group"
          >
            <ProfileImage size="md" user={user} />
            <span className="text-sm font-medium text-neutral-800 dark:text-neutral-100 group-hover:text-primary-500 transition-colors">
              {user.firstName}
              {currentUserId && user.id === currentUserId && (
                <span className="font-normal text-neutral-400 dark:text-neutral-500"> · you</span>
              )}
            </span>
          </PublicProfileButton>
          {isHost && (
            <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded-full bg-primary-500/10 text-primary-600 dark:text-primary-300 text-[11px] font-semibold">
              Host
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

// Prominent primary CTA linking into a kiki's chat. Used for both the group
// chat (accepted) and the one-on-one reach-out chat (pending).
function KikiChatButton({
  href,
  label,
  kikiId,
  chatType,
}: {
  href: string;
  label: string;
  kikiId: string;
  chatType: "group" | "application";
}) {
  return (
    <a
      href={href}
      onClick={() => tracking.kikiChatOpened(kikiId, chatType)}
      className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm transition-colors no-underline"
    >
      <MessageSquare className="w-4 h-4 shrink-0" />
      {label}
    </a>
  );
}

// ---------------------------------------------------------------------------
// Pending — prominent chat CTA + withdraw
// ---------------------------------------------------------------------------

function PendingActions({
  slug,
  requestId,
  applicationId,
}: {
  slug: string;
  requestId: string;
  applicationId: string;
}) {
  const withdraw = useWithdrawBuddyApplication(slug, requestId);
  const toast = useToast();

  const handleWithdraw = async () => {
    try {
      const res = await withdraw.mutateAsync(applicationId);
      if (!res.success) toast.error("Couldn't withdraw");
    } catch {
      toast.error("Couldn't withdraw");
    }
  };

  return (
    <div className="space-y-2">
      <div className="rounded-xl bg-base-200 dark:bg-neutral-800/60 px-4 py-3 text-sm text-neutral-600 dark:text-neutral-300 text-center">
        Reach-out sent — waiting for a response.
      </div>
      <KikiChatButton href={`/app/chat/kiki_app_${applicationId}`} label="Open kiki chat" kikiId={requestId} chatType="application" />
      <button
        type="button"
        onClick={handleWithdraw}
        disabled={withdraw.isPending}
        className="w-full text-xs text-neutral-400 hover:text-rose-500 transition-colors disabled:opacity-50 py-1"
      >
        {withdraw.isPending ? "Withdrawing…" : "Withdraw reach-out"}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Owner — applicant list with accept/decline controls and chat links
// ---------------------------------------------------------------------------

function OwnerActions({
  detail,
  slug,
  userId,
}: {
  detail: BuddyRequestDetail;
  slug: string;
  userId: string | null;
}) {
  const apps = detail.ownerApplications ?? [];
  const pending = apps.filter((a) => a.status === "pending");
  const accepted = apps.filter((a) => a.status === "accepted");
  const declined = apps.filter((a) => a.status === "declined");

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold">People who reached out</h3>

      {apps.length === 0 && (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          When someone reaches out they'll show up here.
        </p>
      )}

      {pending.length > 0 && (
        <ApplicantGroup title="Requests" apps={pending} slug={slug} requestId={detail.id} userId={userId} />
      )}
      {accepted.length > 0 && (
        <ApplicantGroup title="Going with you" apps={accepted} slug={slug} requestId={detail.id} userId={userId} />
      )}
      {declined.length > 0 && (
        <ApplicantGroup title="Declined" apps={declined} slug={slug} requestId={detail.id} userId={userId} dim />
      )}
    </div>
  );
}

function ApplicantGroup({
  title,
  apps,
  slug,
  requestId,
  dim,
}: {
  title: string;
  apps: NonNullable<BuddyRequestDetail["ownerApplications"]>;
  slug: string;
  requestId: string;
  userId: string | null;
  dim?: boolean;
}) {
  return (
    <div className={dim ? "opacity-50" : ""}>
      <p className="text-xs uppercase tracking-wide text-neutral-400 dark:text-neutral-500 mb-2">{title}</p>
      <div className="space-y-2">
        {apps.map((a) => (
          <ApplicantRow key={a.id} app={a} slug={slug} requestId={requestId} />
        ))}
      </div>
    </div>
  );
}

function ApplicantRow({
  app,
  slug,
  requestId,
}: {
  app: NonNullable<BuddyRequestDetail["ownerApplications"]>[number];
  slug: string;
  requestId: string;
}) {
  const update = useUpdateBuddyApplicationStatus(slug, requestId);
  const toast = useToast();

  const setStatus = async (status: BuddyApplicationStatus) => {
    try {
      const res = await update.mutateAsync({ applicationId: app.id, status });
      if (!res.success) { toast.error("Couldn't update"); return; }
      tracking.kikiApplicantDecided(requestId, status === "pending" ? "undo" : status);
    } catch {
      toast.error("Couldn't update");
    }
  };

  return (
    <div className="rounded-xl border border-black/10 dark:border-white/10 overflow-hidden">
      <div className="p-3 flex items-start gap-3">
        <ProfileImage size="md" user={app.user} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">
            {formatNameWithInitial(app.user.firstName, app.user.lastName)}
          </p>
          {app.note && (
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 line-clamp-2">
              {app.note}
            </p>
          )}
          {app.sharedProfile?.interests && app.sharedProfile.interests.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {app.sharedProfile.interests.map((i) => (
                <span
                  key={i}
                  className="px-2 py-0.5 rounded-full bg-base-200 dark:bg-neutral-800 text-[11px] text-neutral-600 dark:text-neutral-300"
                >
                  {i}
                </span>
              ))}
            </div>
          )}
          {app.sharedProfile?.instagram && (
            <a
              href={`https://instagram.com/${app.sharedProfile.instagram}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1.5 inline-flex items-center gap-1 text-xs text-primary-500 hover:text-primary-600 transition-colors"
            >
              <Instagram className="w-3.5 h-3.5" />
              @{app.sharedProfile.instagram}
            </a>
          )}
        </div>
        <div className="shrink-0 flex items-center gap-1">
          {app.status === "pending" ? (
            <>
              <button
                type="button"
                onClick={() => setStatus("declined")}
                disabled={update.isPending}
                aria-label="Decline"
                className="p-2 rounded-full text-neutral-500 hover:text-rose-500 hover:bg-rose-500/10 transition-colors disabled:opacity-50"
              >
                <X className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setStatus("accepted")}
                disabled={update.isPending}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium bg-primary-500 text-white hover:bg-primary-600 transition-colors disabled:opacity-50"
              >
                <Check className="w-3.5 h-3.5" /> Accept
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setStatus("pending")}
              disabled={update.isPending}
              className="inline-flex items-center gap-1 text-xs text-neutral-400 hover:text-primary-500 transition-colors disabled:opacity-50"
            >
              <Undo2 className="w-3.5 h-3.5" /> Undo
            </button>
          )}
        </div>
      </div>
      {/* Chat link per applicant */}
      <div className="border-t border-black/10 dark:border-white/10">
        <a
          href={`/app/chat/kiki_app_${app.id}`}
          onClick={() => tracking.kikiChatOpened(requestId, "application")}
          className="flex items-center gap-2 px-3 py-2 text-xs text-neutral-500 hover:text-primary-500 hover:bg-base-200/50 dark:hover:bg-neutral-800/40 transition-colors no-underline"
        >
          <MessageSquare className="w-3.5 h-3.5 shrink-0" />
          Open chat
        </a>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Accepted — confirmation badge + face stack
// ---------------------------------------------------------------------------

function AcceptedBadge({ posterName }: { posterName: string }) {
  return (
    <div className="rounded-xl bg-primary-500/10 text-primary-700 dark:text-primary-300 px-4 py-3 text-sm flex items-center gap-2">
      <Check className="w-4 h-4 shrink-0" />
      You're going with {posterName}!
    </div>
  );
}

// ---------------------------------------------------------------------------
// Visitor / declined — reach-out form
// ---------------------------------------------------------------------------

function VisitorActions({
  detail,
  slug,
  isSignedIn,
  openSignIn,
}: {
  detail: BuddyRequestDetail;
  slug: string;
  isSignedIn: boolean;
  openSignIn: () => void;
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const wasDeclined = detail.viewerStatus === "declined";

  if (wasDeclined) {
    return (
      <div className="rounded-xl bg-base-200 dark:bg-neutral-800/60 px-4 py-3 text-sm text-neutral-600 dark:text-neutral-300">
        {detail.user.firstName} found someone else for this one.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => (isSignedIn ? setModalOpen(true) : (tracking.kikiSignInClicked("detail"), openSignIn()))}
        className="btn btn-primary w-full inline-flex items-center justify-center gap-2"
      >
        <Send className="w-4 h-4" />
        Reach out to join
      </button>
      <p className="text-center text-xs text-neutral-400 dark:text-neutral-500">
        Add a note and choose what to share with {detail.user.firstName}
      </p>

      <ReachOutModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        slug={slug}
        requestId={detail.id}
        posterName={detail.user.firstName}
      />
    </div>
  );
}
